/**
 * Sign in and create an account.
 *
 * One screen with two modes rather than two routes: the forms differ by a single field, and
 * a person who mistypes their password on a site they have not signed up for needs the
 * other option in front of them, not one navigation away.
 *
 * Nothing here touches a credential store. The password is posted once and forgotten; the
 * session comes back as an httpOnly cookie this code cannot read.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'

import { Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, Gift, ShieldCheck } from 'lucide-react'
import { motion, Variants } from 'framer-motion'

import { ProductMark } from '@/components/shell/Sidebar'
import { Button } from '@/components/ui/Controls'
import { PasswordToggle } from '@/components/ui/PasswordToggle'
import { env } from '@/config/env'
import { cn } from '@/lib/utils/cn'
import { DEFAULT_FREE_CREDITS } from '@/features/billing/plans'
import { useAuth } from '@/features/auth/useAuth'
import { toApiError } from '@/lib/api/errors'

const MIN_PASSWORD_LENGTH = 10

type Mode = 'signin' | 'signup'

function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  hint,
  required = true,
}: {
  label: string
  type: string
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  hint?: string
  required?: boolean
}) {
  const id = useId()
  const [revealed, setRevealed] = useState(false)
  const isPassword = type === 'password'

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-dim">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={isPassword && revealed ? 'text' : type}
          value={value}
          required={required}
          autoComplete={autoComplete}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onChange={(event) => onChange(event.target.value)}
          className={cn('field h-10', isPassword && 'pr-10')}
        />
        {isPassword ? (
          <PasswordToggle revealed={revealed} onToggle={() => setRevealed((on) => !on)} />
        ) : null}
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 block text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  )
}

function useGoogleOutcome(): { outcome: string | null; detail: string | null } {
  const [state] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return { outcome: params.get('auth'), detail: params.get('detail') }
  })

  useEffect(() => {
    if (!state.outcome) return
    const url = new URL(window.location.href)
    url.searchParams.delete('auth')
    url.searchParams.delete('detail')
    window.history.replaceState({}, '', url.toString())
  }, [state.outcome])

  return state
}

export function SignInPage({ initialMode = 'signin' }: { initialMode?: Mode } = {}) {
  const { signIn, signUp } = useAuth()
  const google = useGoogleOutcome()
  const [mode, setMode] = useState<Mode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const shown = error ?? (google.outcome === 'error' ? google.detail : null)
  const isSignUp = mode === 'signup'

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 15, scale: 0.98 },
    visible: { 
      opacity: 1, y: 0, scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 } 
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (isSignUp) {
        await signUp(email, password, displayName)
      } else {
        await signIn(email, password)
      }
    } catch (caught) {
      setError(toApiError(caught).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-ground px-4 py-12 relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0, x: -15 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, type: 'spring' }}
        className="absolute left-4 top-4 sm:left-8 sm:top-8"
      >
        <Link
          to="/"
          className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        >
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          Back to home
        </Link>
      </motion.div>
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-[26rem]"
      >
        <motion.form
          variants={itemVariants}
          onSubmit={onSubmit}
          className="space-y-5 rounded-xl border border-line bg-surface px-6 py-8 shadow-pop sm:px-10"
        >
          <div className="text-center">
            <ProductMark className="mx-auto h-10 w-10" />
            <h1 className="mt-4 text-xl font-semibold text-ink">RepoPilot</h1>
            <p className="mt-1.5 text-sm text-ink-muted">
              {isSignUp
                ? 'Create an account, then connect your own GitHub.'
                : 'Sign in to reach your repositories and tool history.'}
            </p>
            {isSignUp ? (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                data-testid="signup-free-credits"
                className="mt-3 flex items-center justify-center gap-1.5 rounded-lg border
                  border-ok/25 bg-ok-wash px-3 py-2 text-xs text-ink-dim"
              >
                <Gift aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ok" />
                <span>
                  <span className="font-medium text-ink">
                    {DEFAULT_FREE_CREDITS} free credits
                  </span>{' '}
                  to start. No card required.
                </span>
              </motion.p>
            ) : null}
          </div>

          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            hint={isSignUp ? `At least ${MIN_PASSWORD_LENGTH} characters.` : undefined}
          />
          {isSignUp ? (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Field
                label="Display name"
                type="text"
                value={displayName}
                onChange={setDisplayName}
                autoComplete="name"
                required={false}
                hint="Optional. Shown in the sidebar."
              />
            </motion.div>
          ) : null}

          {shown ? (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-fail/25 bg-fail-wash px-3
                py-2.5 text-sm text-fail"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{shown}</span>
            </motion.p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            disabled={busy || !email || password.length < (isSignUp ? MIN_PASSWORD_LENGTH : 1)}
            className="h-10 w-full"
          >
            {busy ? 'Working…' : isSignUp ? 'Create account' : 'Sign in'}
          </Button>

          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-line" />
            <span className="text-xs text-ink-faint">or</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <a
            href={`${env.apiBaseUrl}/api/auth/google/start`}
            className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-md border
              border-line-strong bg-surface px-3 text-sm font-medium text-ink shadow-card
              transition-colors hover:border-ink-faint hover:bg-hover"
          >
            <GoogleMark />
            Continue with Google
          </a>
        </motion.form>

        <motion.p variants={itemVariants} className="mt-6 text-center text-sm text-ink-muted">
          {isSignUp ? 'Already have an account?' : 'No account yet?'}{' '}
          <button
            type="button"
            className="font-medium text-primary-bright underline-offset-2 hover:underline"
            onClick={() => {
              setMode(isSignUp ? 'signin' : 'signup')
              setError(null)
            }}
          >
            {isSignUp ? 'Sign in' : 'Create one'}
          </button>
        </motion.p>

        <motion.p variants={itemVariants} className="mt-4 text-center text-xs text-ink-muted">
          <Link to="/privacy" className="underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          {' · '}
          <Link to="/terms" className="underline-offset-2 hover:underline">
            Terms of Service
          </Link>
        </motion.p>

        <motion.p variants={itemVariants} className="mx-auto mt-6 max-w-xs text-center text-xs leading-relaxed text-ink-faint">
          <ShieldCheck aria-hidden="true" className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
          Your GitHub credential is held by the server, encrypted, and is never sent to this page.
        </motion.p>
      </motion.div>
    </div>
  )
}
